import {
  Button,
  Group,
  Loader,
  Pill,
  SimpleGrid,
  Table,
  Title,
  Text,
  Tooltip,
} from "@mantine/core";
import { useQuery } from "@apollo/client";
import { PropsWithChildren, useState, useEffect, useMemo } from "react";
import { format, subDays } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { FeatureToggle } from "shared/src/constants/featureToggleNames";
import { getAppealCurrentStatusLabel } from "shared/src/constants/appealCurrentStatus";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { parseDateOnly, DATE_ONLY_FORMAT } from "shared/src/utils/dateUtils";
import { gql } from "@/__generated__";
import {
  Maybe,
  ProviderReferralsQuery,
  AppealCurrentStatus,
  ReferralSortField,
  SortDirection,
  SponsoringParty,
} from "@/__generated__/graphql";
import CheckboxIndicator from "../CheckboxIndicator";
import { ReferralActionsDropdown } from "../ReferralActionsDropdown";
import { AppealOutcomeUpdateModal } from "../AppealOutcomeUpdateModal";
import { SearchBar } from "../SearchBar";
import { SortableHeader } from "../SortableHeader";
import { useSearch } from "../../hooks/useSearch";
import InlineBanner from "../shared/InlineBanner";

import styles from "./referral-table.module.scss";
import { useProviderHasFacilities } from "@/hooks/useProviderHasFacilities.ts";
import { useIsEnterpriseProvider } from "@/hooks/useIsEnterpriseProvider.ts";

export const GET_REFERRALS = gql(`
  query ProviderReferrals($limit: Int, $offset: Int, $filter: ReferralsFilterInput, $sort: ReferralsSortInput) {
    referrals(limit: $limit, offset: $offset, filter: $filter, sort: $sort) {
      referrals {
        referralId
        patientEmail
        referringUser {
          providerUserId
          firstName
          lastName
          email
        }
        appeal {
          appealId
          referringProviderName
          createdTimestamp
          updatedTimestamp
          sponsoringParty
          isExpeditedReview
          status {
            referred
            insuranceCardUpload
            insuranceCardUploadSkipped
            paymentSuccessful
            issueOutstanding
            denialUpheld
            denialOverturned
            appealSubmitted
            closed
            currentStatus
            updatedTimestamp
          }
          patient {
            firstName
            lastName
            dateOfBirth
            externalId
          }
          referringFacilityName,
        }
      }
      hasNextPage
    }
    provider {
      facilities {
        providerFacilityId
      }
    }
  }
`);

const PAGE_SIZE = 20;

type IndicatorTooltipProps = {
  sponsoringParty: SponsoringParty;
};

const IndicatorTooltip = ({
  sponsoringParty,
  children,
}: PropsWithChildren<IndicatorTooltipProps>) => (
  <Tooltip
    label={`Sponsored by ${sponsoringParty === SponsoringParty.Claimable ? "Claimable" : "your practice"}`}
    withArrow
    arrowSize={16}
  >
    {children}
  </Tooltip>
);

type SponsoredAppealIndicatorProps = {
  sponsoringParty?: Maybe<SponsoringParty>;
  isExpired: boolean;
  isAppealSubmitted: boolean;
};

const SponsoredAppealIndicator = ({
  sponsoringParty,
  isExpired,
  isAppealSubmitted,
}: SponsoredAppealIndicatorProps) => {
  if (!sponsoringParty) {
    return null;
  }

  if (isExpired) {
    return <CheckboxIndicator variant="x" />;
  }

  if (!isAppealSubmitted) {
    return (
      <IndicatorTooltip sponsoringParty={sponsoringParty}>
        <CheckboxIndicator variant="disabled-check" />
      </IndicatorTooltip>
    );
  }

  return (
    <IndicatorTooltip sponsoringParty={sponsoringParty}>
      <CheckboxIndicator variant="check" />
    </IndicatorTooltip>
  );
};

const STATUS_PILL_STYLES: Record<string, string> = {
  [AppealCurrentStatus.Submitted]: styles.submitted,
  [AppealCurrentStatus.Upheld]: styles.upheld,
  [AppealCurrentStatus.Referred]: styles.referred,
  [AppealCurrentStatus.ReferralInProgress]: styles.referralInProgress,
  [AppealCurrentStatus.Expired]: styles.expired,
  [AppealCurrentStatus.Overturned]: styles.overturned,
  [AppealCurrentStatus.InSupport]: styles.support,
  [AppealCurrentStatus.Closed]: styles.closed,
  [AppealCurrentStatus.InProgress]: styles.inProgress,
};

const VIEWABLE_STATUSES: string[] = [
  AppealCurrentStatus.Overturned,
  AppealCurrentStatus.Submitted,
  AppealCurrentStatus.Upheld,
];

const UPDATABLE_OUTCOME_STATUSES: string[] = [
  AppealCurrentStatus.Submitted,
  AppealCurrentStatus.Overturned,
  AppealCurrentStatus.Upheld,
];

type ReferralTableProps = {
  showTitle?: boolean;
};

const ReferralTable = ({ showTitle }: ReferralTableProps) => {
  const [activePage, setPage] = useState(1);
  const providerHasFacilities = useProviderHasFacilities();
  const isEnterpriseProvider = useIsEnterpriseProvider();
  const [selectedAppealId, setSelectedAppealId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const navigate = useNavigate();
  const isRowNavigationEnabled = useFeatureFlagEnabled(
    FeatureToggle.APPEAL_HUB_REFERRAL_FLOW_V2,
  );
  const { user } = useAuthenticator();
  const [showReferredByMe, setShowReferredByMe] = useState<boolean>(false);
  const [showRecentOutcomes, setShowRecentOutcomes] = useState<boolean>(false);
  const [sortField, setSortField] = useState<ReferralSortField | null>(null);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC" | null>(
    null,
  );
  const [dateRange, setDateRange] = useState<[string | null, string | null]>([
    null,
    null,
  ]);

  const {
    searchCriteria,
    handleSearch: updateSearchCriteria,
    clearAllSearch,
    hasActiveSearch,
  } = useSearch();

  const [statusFilter, setStatusFilter] = useState<AppealCurrentStatus[]>([]);

  const filter = useMemo(() => {
    const baseFilter: {
      patientFirstName?: string;
      patientLastName?: string;
      patientDateOfBirth?: string;
      patientExternalId?: string;
      status?: AppealCurrentStatus[];
      referringUser?: string[];
      appealStatusChangedDateFrom?: string;
      appealStatusChangedDateTo?: string;
    } = {};

    if (searchCriteria.patientFirstName) {
      baseFilter.patientFirstName = searchCriteria.patientFirstName;
    }
    if (searchCriteria.patientLastName) {
      baseFilter.patientLastName = searchCriteria.patientLastName;
    }
    if (searchCriteria.patientDateOfBirth) {
      baseFilter.patientDateOfBirth = searchCriteria.patientDateOfBirth;
    }
    if (searchCriteria.patientExternalId) {
      baseFilter.patientExternalId = searchCriteria.patientExternalId;
    }
    if (statusFilter.length > 0) {
      baseFilter.status = statusFilter;
    }
    if (showReferredByMe && user?.userId) {
      baseFilter.referringUser = [user.userId];
    }
    if (dateRange[0]) {
      baseFilter.appealStatusChangedDateFrom = new Date(
        dateRange[0],
      ).toISOString();
    }
    if (dateRange[1]) {
      const endDate = new Date(dateRange[1]);
      endDate.setUTCHours(23, 59, 59, 999);
      baseFilter.appealStatusChangedDateTo = endDate.toISOString();
    }

    return Object.keys(baseFilter).length > 0 ? baseFilter : undefined;
  }, [searchCriteria, statusFilter, showReferredByMe, user?.userId, dateRange]);

  const sort = useMemo(
    () =>
      sortField && sortDirection
        ? { field: sortField, direction: sortDirection as SortDirection }
        : undefined,
    [sortField, sortDirection],
  );

  const { loading, error, data, refetch } = useQuery(GET_REFERRALS, {
    fetchPolicy: "no-cache",
    variables: {
      limit: PAGE_SIZE,
      offset: (activePage - 1) * PAGE_SIZE,
      filter,
      sort,
    },
  });

  useEffect(() => {
    setPage(1);
  }, [filter, sort]);

  const handleStatusFilterChange = (statuses: string[]) => {
    setStatusFilter(statuses as AppealCurrentStatus[]);
  };

  const deactivateRecentOutcomesPreset = () => {
    setShowRecentOutcomes(false);
    setStatusFilter([]);
    setDateRange([null, null]);
    setSortField(null);
    setSortDirection(null);
  };

  const handleResetFilters = () => {
    deactivateRecentOutcomesPreset();
    setShowReferredByMe(false);
  };

  const handleReferredByMeChange = (isActive: boolean) => {
    setShowReferredByMe(isActive);
  };

  const handleDateRangeChange = (range: [string | null, string | null]) => {
    setDateRange(range);
    if (range[0] === null && range[1] === null && showRecentOutcomes) {
      deactivateRecentOutcomesPreset();
    }
  };

  const handleSort = (field: string, direction: "ASC" | "DESC" | null) => {
    setSortField(direction ? (field as ReferralSortField) : null);
    setSortDirection(direction);
  };

  const handleRecentOutcomesChange = (isActive: boolean) => {
    if (isActive) {
      setShowRecentOutcomes(true);
      setStatusFilter([
        AppealCurrentStatus.Overturned,
        AppealCurrentStatus.Upheld,
      ]);
      const today = new Date();
      const threeDaysAgo = subDays(today, 3);
      setDateRange([
        format(threeDaysAgo, DATE_ONLY_FORMAT),
        format(today, DATE_ONLY_FORMAT),
      ]);
      setSortField(ReferralSortField.AppealStatusChangedDate);
      setSortDirection("DESC");
    } else {
      deactivateRecentOutcomesPreset();
    }
  };

  const refreshCurrentPage = async () => {
    await refetch();
  };

  const handleUpdateAppealOutcome = (
    referral: ProviderReferralsQuery["referrals"]["referrals"][number],
  ) => {
    setSelectedAppealId(referral.appeal.appealId);
  };

  const handleModalSuccess = refreshCurrentPage;

  const handleViewError = (errorMessage: string) => {
    setDownloadError(errorMessage);
  };

  const handleRowClick = (appealId: string) => {
    if (!isRowNavigationEnabled) return;
    navigate(`/appeals/${appealId}/strengthen/patient`);
  };

  const handleRowKeyDown = (
    event: React.KeyboardEvent<HTMLTableRowElement>,
    appealId: string,
  ) => {
    if (!isRowNavigationEnabled) return;
    if (event.key === "Enter") {
      navigate(`/appeals/${appealId}/strengthen/patient`);
    }
  };

  const statusToPill = (currentStatus: string) => {
    const rootStyle = STATUS_PILL_STYLES[currentStatus] ?? styles.inProgress;
    const label = getAppealCurrentStatusLabel(currentStatus);

    return (
      <Pill
        classNames={{
          label: styles.statusLabel,
          root: rootStyle,
        }}
      >
        {label}
      </Pill>
    );
  };

  const rows = (referrals: ProviderReferralsQuery["referrals"]["referrals"]) =>
    referrals.map((referral) => {
      const currentStatus = referral.appeal.status.currentStatus ?? "";
      return (
        <Table.Tr
          key={referral.referralId}
          onClick={
            isRowNavigationEnabled
              ? () => handleRowClick(referral.appeal.appealId)
              : undefined
          }
          onKeyDown={
            isRowNavigationEnabled
              ? (e) => handleRowKeyDown(e, referral.appeal.appealId)
              : undefined
          }
          tabIndex={isRowNavigationEnabled ? 0 : undefined}
          className={isRowNavigationEnabled ? styles.clickableRow : undefined}
        >
          <Table.Td>{referral.appeal.appealId}</Table.Td>
          <Table.Td>
            <SimpleGrid cols={1} verticalSpacing={0}>
              <Text
                className={styles.patientName}
              >{`${referral.appeal.patient?.firstName} ${referral.appeal.patient?.lastName}`}</Text>
              <Text className={styles.patientEmail}>
                {referral.patientEmail}
              </Text>
            </SimpleGrid>
          </Table.Td>
          <Table.Td>
            {referral.appeal.patient?.dateOfBirth
              ? format(
                  parseDateOnly(referral.appeal.patient.dateOfBirth),
                  "MM/dd/yyyy",
                )
              : "—"}
          </Table.Td>
          <Table.Td>{referral.appeal.patient?.externalId || "—"}</Table.Td>
          <Table.Td>
            {referral.appeal.isExpeditedReview == null
              ? "—"
              : referral.appeal.isExpeditedReview
                ? "Yes"
                : "No"}
          </Table.Td>
          <Table.Td>
            {referral.referringUser ? (
              referral.referringUser.firstName &&
              referral.referringUser.lastName ? (
                <SimpleGrid cols={1} verticalSpacing={0}>
                  <Text
                    className={styles.referringUserName}
                  >{`${referral.referringUser.firstName} ${referral.referringUser.lastName}`}</Text>
                  <Text className={styles.referringUserEmail}>
                    {referral.referringUser.email}
                  </Text>
                </SimpleGrid>
              ) : (
                <Text className={styles.referringUserEmail}>
                  {referral.referringUser.email}
                </Text>
              )
            ) : (
              <em>{"—"}</em>
            )}
          </Table.Td>
          <Table.Td>
            {format(new Date(referral.appeal.createdTimestamp), "MM/dd/yyyy")}
          </Table.Td>
          <Table.Td>
            {referral.appeal.status.updatedTimestamp
              ? format(
                  new Date(referral.appeal.status.updatedTimestamp),
                  "MM/dd/yyyy",
                )
              : "—"}
          </Table.Td>
          {providerHasFacilities && (
            <Table.Td>
              {referral.appeal.referringFacilityName || <em>{"—"}</em>}
            </Table.Td>
          )}
          {!isEnterpriseProvider && (
            <Table.Td align="center">
              <SponsoredAppealIndicator
                sponsoringParty={referral.appeal.sponsoringParty}
                isAppealSubmitted={Boolean(
                  referral.appeal.status.appealSubmitted,
                )}
                isExpired={referral.appeal.status.currentStatus === "EXPIRED"}
              />
            </Table.Td>
          )}
          <Table.Td>{statusToPill(currentStatus)}</Table.Td>
          <Table.Td
            className={styles.actionsCell}
            onClick={(e) => e.stopPropagation()}
          >
            <ReferralActionsDropdown
              appeal={referral.appeal}
              onUpdateOutcome={() => handleUpdateAppealOutcome(referral)}
              onViewError={handleViewError}
              onCloseSuccess={refreshCurrentPage}
              canView={VIEWABLE_STATUSES.includes(currentStatus)}
              canUpdateOutcome={UPDATABLE_OUTCOME_STATUSES.includes(
                currentStatus,
              )}
              canClose={!referral.appeal.status.closed}
            />
          </Table.Td>
        </Table.Tr>
      );
    });

  return (
    <div className={styles.tableContainer}>
      {showTitle && (
        <Title order={1} className={styles.center}>
          {"My Appeals"}
        </Title>
      )}
      {downloadError && (
        <InlineBanner
          type="error"
          message={downloadError}
          dismissable
          onDismiss={() => setDownloadError(null)}
        />
      )}
      {loading && <Loader color="black" />}
      {!loading &&
        !error &&
        data &&
        data.referrals.referrals.length === 0 &&
        activePage === 1 &&
        "Start referring patients to track their appeals here"}
      {!loading && !error && (
        <>
          <SearchBar
            search={{
              onSearch: updateSearchCriteria,
              onClear: clearAllSearch,
              criteria: searchCriteria,
              hasActiveSearch,
            }}
            filters={{
              onStatusChange: handleStatusFilterChange,
              onReset: handleResetFilters,
              selectedStatuses: statusFilter,
              showReferredByMe,
              onReferredByMeChange: handleReferredByMeChange,
              dateRange,
              onDateRangeChange: handleDateRangeChange,
              showRecentOutcomes,
              onRecentOutcomesChange: handleRecentOutcomesChange,
            }}
          />
          <Table
            highlightOnHover
            classNames={{ table: styles.table, td: styles.td }}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{"Appeal Id"}</Table.Th>
                <Table.Th>{"Patient"}</Table.Th>
                <Table.Th>{"Date of Birth"}</Table.Th>
                <Table.Th>{"External ID"}</Table.Th>
                <Table.Th>{"Expedited"}</Table.Th>
                <Table.Th>{"Referred By"}</Table.Th>
                <Table.Th>{"Referral Sent"}</Table.Th>
                <SortableHeader
                  label="Status Changed Date"
                  sortField={ReferralSortField.AppealStatusChangedDate}
                  currentSortField={sortField}
                  currentSortDirection={sortDirection}
                  onSort={handleSort}
                />
                {providerHasFacilities && <Table.Th>{"Facility"}</Table.Th>}
                {!isEnterpriseProvider && (
                  <Table.Th className={styles.centerTH}>{"Sponsored"}</Table.Th>
                )}
                <Table.Th>{"Appeal Status"}</Table.Th>
                <Table.Th className={styles.actionsColumn}>
                  {"Actions"}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            {data && data.referrals.referrals.length > 0 && (
              <Table.Tbody>{rows(data.referrals.referrals)}</Table.Tbody>
            )}
          </Table>
          <Group justify="center" className={styles.pagination}>
            {activePage > 1 && (
              <Button
                variant="default"
                size="sm"
                onClick={() => setPage(activePage - 1)}
              >
                {"Previous"}
              </Button>
            )}
            {data?.referrals.hasNextPage && (
              <Button
                variant="default"
                size="sm"
                onClick={() => setPage(activePage + 1)}
              >
                {"Next"}
              </Button>
            )}
          </Group>
        </>
      )}

      {selectedAppealId && (
        <AppealOutcomeUpdateModal
          onClose={() => {
            setSelectedAppealId(null);
          }}
          appealId={selectedAppealId}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

export default ReferralTable;
